import { Component, Input, OnChanges, Injector, SimpleChanges, OnDestroy, OnInit, AfterViewChecked } from '@angular/core';
import { moveItemInArray, CdkDragDrop } from '@angular/cdk/drag-drop';
import { AbsiBuilderService } from '../../absi-builder.service';
import { MatDialogConfig } from '@angular/material/dialog';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { ChecklistBuilderService } from 'src/app/components/checklists/checklist-builder/checklist-builder.service';
import { WFSectionConfig, WFSectionLocalResource } from 'src/app/components/workflow/workflow';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { WfSignatureResource, WfSignature, WFTaskSignatureResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ClEditorComponent } from 'src/app/controls/checklist-components/cl-editor/cl-editor.component';
import { FormStatus } from 'src/app/services/forms/form-status';
import { Subscription } from 'rxjs';
import { AbsiTemplateSectionRefresh, AbsiTemplateSectionTaskDelete } from '../../../store/absi-template-sections/absi-template-section.action';

@Component({
  selector: 'absi-builder-section',
  templateUrl: './absi-builder-section.component.html',
  styleUrls: ['./absi-builder-section.component.scss']
})
export class AbsiBuilderSectionComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

  @Input() section!: WFSectionConfig;
  @Input() wfTableId!: number;
  @Input() disabled!: boolean;
  @Input() absiTemplateID!: number;
  @Input() serialNo!: string;

  public chbTypes: Array<number> = [4, 5];

  sectionName!: string | null;

  adding!: boolean;
  editing!: boolean;
  editingId!: number;
  formType = FormType[FormType.ABSI];

  wfSignatures: WfSignatureResource[] = [];
  signatures!: WfSignature[];
  wfTaskSignature: WFTaskSignatureResource = new WFTaskSignatureResource();

  formStatuses?: FormStatusEnum[];
  approvals!: number;

  controlOptions: any[] = [
    { label: 'Checkbox', value: 4 },
    { label: 'Note', value: 7 },
    { label: 'Checkbox & Text', value: 8 },
    { label: 'Schedule & Checkbox', value: 9 }
  ];

  wfSectionLocal?: WFSectionLocalResource;
  wfSectionLocals?: WFSectionLocalResource[];
  wfSectionLocalsFiltered?: WFSectionLocalResource[];
  wfSectionLocalsSubs!: Subscription;
  private sectionRefreshKey?: string;
  private sectionRefreshRequested = false;

  constructor(
    protected override injector: Injector,
    private _builder: AbsiBuilderService,
    public _gBuilder: ChecklistBuilderService,
  ) {
    super(injector);
  }

  ngAfterViewChecked(): void {
  }

  ngOnInit(): void {
    // this.loadWFSectionLocals();
  }

  override ngOnDestroy(): void {
    this.wfSectionLocalsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadWFSectionLocals();
  }

  /**
   * Subscribes to the section-task slice for this section and dispatches a
   * targeted refresh when the store does not yet have the data for the table.
   */
  loadWFSectionLocals() {
    if (!this.wfTableId || !this.section?.number) {
      this.create();
      return;
    }

    const refreshKey = `${this.wfTableId}:${this.section.number}`;
    if (this.sectionRefreshKey !== refreshKey) {
      this.sectionRefreshKey = refreshKey;
      this.sectionRefreshRequested = false;
    }

    this.wfSectionLocalsSubs?.unsubscribe();
    this.wfSectionLocalsSubs = this.store.select(state => state.AbsiTemplateSections.data).subscribe((data: WfSignatureResource[]) => {
      this.wfSignatures = data.filter(s => s.sectionNumber == this.section.number && s.wfTaskLocal?.wfStepLocal?.wfSteps.at(-1)?.wfTableID == this.wfTableId);
      if (!this.wfSignatures?.length && !this.sectionRefreshRequested) {
        // Empty can mean "not loaded yet"; dispatch once per section/table pair.
        this.sectionRefreshRequested = true;
        this.store.dispatch(new AbsiTemplateSectionRefresh(this.wfTableId, this.section.number));
      }
      else {
        this.refresh();
      }
    });
  }

  create() {
    this.wfSignatures = [];
    this.signatures = [];
    this.sectionName = null;
  }

  /**
   * Converts workflow signature resources into the editor row model used by the
   * checklist task UI, including validation state and display numbering.
   */
  refresh() {
    if (this.wfTableId == undefined || this.wfTableId == 0) {
      this.create();
      return;
    }

    this.sectionName = this.wfSignatures[0] ? this.wfSignatures[0].sectionName : null;
    const statuses = this.wfSignatures[0]?.wfTaskLocal?.wfStepLocal?.wfStepConfig?.wfStepStatusConfigs?.map(s => s.status) ?? [];
    this.formStatuses = statuses.filter(s => s) as FormStatusEnum[];

    let index = 1;

    this.signatures = [];
    this.approvals = this.wfSignatures.length;
    this.wfSignatures.forEach(t => {
      const options: any[] = t.options ? JSON.parse(t.options) : [];
      const number = this.section.number + '.' + index;
      if (!t.wfTaskLocal.componentID) t.wfTaskLocal.componentID = t.type;
      const errors = this.getErrors(t.wfTaskLocal);
      const signature: WfSignature = {
        id: t.id,
        number: t.required ? number : null,
        approveActionId: t.approveID,
        unapproveActionId: t.unapproveID,
        disapproveActionId: t.disapproveID,
        amendmentId: t.amendmentID,
        name: t.titleBefore,
        question: t.question,
        question2: t.titleAfter,
        question3: t.titleAfter2,
        roles: t.roles.map(r => r.id),
        roleCodes: t.roles.map(r => r.code).join('/'),
        disabled: true,
        approvedBy: t.signedBy,
        approved: t.signed,
        approvedOn: t.signedOn,
        approveAction: t.approveID != 0,
        unapproveAction: t.unapproveID != 0,
        disapproveAction: t.disapproveID != 0,
        amendment: t.amendmentEnabled,
        required: t.required,
        taskId: t.taskID,
        value: t.value,
        value2: t.numericValue,
        type: t.type,
        options,
        sectionName: t.sectionName,
        iterable: t.iterable && (t.wfTaskLocal.name3?.includes('{KEStep}') || t.wfTaskLocal.name2?.includes('{vacuum}')),
        order: t.order,
        textValue: t.text,
        visible: t.visible,
        scheduleResourceId: t.scheduleResourceId,
        dateValue1: t.dateValue1,
        dateValue2: t.dateValue2,
        dateValue3: t.dateValue3 != null && t.dateValue3.toString() != '0001-01-01T00:00:00' ? t.dateValue3 : undefined,
        scheduleName: t.scheduleName,
        locationName: t.locationName,
        editMode: false,
        component: t.component,
        configuration: t.configuration,
        condition: t.condition,
        code: t.code,
        invalid: !!errors?.errors?.length,
        errors: errors?.errors.join(', '),
        wfTaskLocal: t.wfTaskLocal
      };
      index++;
      this.signatures.push(signature);
    });
    this.renumberArray();
  }

  /**
   * Opens the checklist editor for either an existing task or a new task seeded
   * from the last task in the current workflow step.
   */
  edit(id?: number) {
    const order = (this.signatures[this.signatures.length - 1]?.order ?? 0) + 1;
    let signature: WfSignature | undefined = {
      name: '[New Name]',
      options: [],
      roles: [],
      required: true,
      approveAction: true,
      unapproveAction: true,
      order,
      stepId: this.wfSignatures[this.wfSignatures.length - 1].wfTaskLocal.wfStepLocalID
    };

    if (id)
      signature = this.signatures.find((x) => x.id === id);

    if (signature) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        s: signature,
        formType: FormType.ABSI,
        serialNo: this.serialNo,
        roles: this.wfSignatures.find(x => x.id === signature?.id)?.roles,
        disabled: this.disabled
      };
      dialogConfig.width = '80vw';
      dialogConfig.autoFocus = false;
      dialogConfig.panelClass = 'draggable-dialog';
      const dialogRef = this.dialog.open(ClEditorComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          if (id) {
            const index = this.signatures.findIndex(s => s.id == id);
            if (index >= 0)
              this.signatures[index] = data.s;
            this._gBuilder.updateWFTaskLocal(data).subscribe();
          }
          else {
            this._gBuilder.createWFTaskLocal(data).subscribe(() => {
              this.refresh();
            });
          }
        }
      });
    }
  }

  delete(task: WfSignature) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('GenChklistBldr_DeleteTaskConfimation').description,
        icon: 'stop'
      }
    });

    confirm.afterClosed().subscribe(data => {
      if (data) {
        this._gBuilder.deleteTask(task.wfTaskLocal ?? task as WfTaskLocalResource).subscribe(() => {
          this.removeDeletedTask(task);
        });
      }
    });
  }

  /** Removes a deleted task from local state and the section store before repainting. */
  private removeDeletedTask(task: WfSignature) {
    const wfTaskLocalID = task.wfTaskLocal?.id;
    if (!wfTaskLocalID) {
      this.refresh();
      return;
    }

    this.wfSignatures = this.wfSignatures.filter(s => s.wfTaskLocal?.id !== wfTaskLocalID);
    this.signatures = this.signatures.filter(s => s.wfTaskLocal?.id !== wfTaskLocalID);
    this.store.dispatch(new AbsiTemplateSectionTaskDelete(this.wfTableId, this.section.number, wfTaskLocalID));
    this.refresh();
  }

  changeRequired(e: any, taskId: number) {
    const required = e.checked;
    this.renumberArray();
    this._builder.SaveRequired(taskId, required).subscribe();
  }

  saveOrders(changed: WfTaskLocalResource[]) {
    this._gBuilder.updateTasksOrder(changed).subscribe();
  }

  /**
   * Normalizes persisted order values after edits or drag/drop and recalculates
   * only the visible required numbering shown to users.
   */
  renumberArray() {
    let index = 1;
    let order = 1;
    let changed: WfTaskLocalResource[] = [];

    this.signatures.forEach(s => {
      if (order != s.order) {
        s.wfTaskLocal!.order = order;
        changed.push(s.wfTaskLocal!);
      }
      s.order = order;
      order++;

      if (s.required && s.component?.numericHeader) {
        s.number = this.section.number + '.' + index;
        index++;
      }
      else {
        s.number = null;
      }
    });
    if (changed.length) {
      this.saveOrders(changed);
    }
  }

  /** Applies drag/drop ordering and persists the changed task order. */
  reorderTable(event: CdkDragDrop<WfSignature[]>) {
    if (event?.previousIndex !== event?.currentIndex) {
      moveItemInArray(this.signatures, event.previousIndex, event.currentIndex);
      this.renumberArray();
    }
  }

  getErrors(task: WfTaskLocalResource) {
    return this._gBuilder.hasTaskErrors(task, []);
  }

}
